import React, { useCallback, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AlertCircle,
  BadgeCheck,
  BarChart3,
  Bell,
  BookOpen,
  Briefcase,
  Building2,
  ChevronLeft,
  ChevronRight,
  Clock,
  CreditCard,
  Database,
  IndianRupee,
  Download,
  FileText,
  Globe,
  HelpCircle,
  Landmark,
  LayoutDashboard,
  LineChart,
  Newspaper,
  type LucideIcon,
  Percent,
  PieChart,
  Search,
  Settings,
  Shield,
  ShieldCheck,
  TrendingUp,
  User,
  Users,
  WalletCards,
  Target,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { decryptData } from '@/lib/utils/encryption';
import { cn } from '@/lib/utils/utils';
import ArhamLogo from '@/assets/images/03-AP CONNECT LOGO ARHAM.svg';
import SmallArhamLogo from '@/assets/images/01-Arham Logo.svg';
import { useAppSelector } from '@/store/hooks';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { IPO_BETA_ENABLED } from '@/utils/ipoFeatureFlag';

interface SidebarProps {
  className?: string;
  onNavigate?: () => void;
}

interface NavItem {
  id: string;
  title: string;
  icon?: LucideIcon;
  href?: string;
  children?: NavItem[];
  badge?: string;
  comingSoon?: boolean;
  level?: number;
}

// Icon mapping for better performance and maintainability
const ICONS = {
  dashboard: LayoutDashboard,
  profile: User, // User profile icon
  kyc: BadgeCheck, // KYC verification badge
  accounts: Users, // Multiple users for accounts
  revenue: IndianRupee, // Revenue tracking
  aging: Clock, // Account aging
  latePayment: AlertCircle, // Late payment alerts
  reports: FileText, // Reports and documents
  ledger: BookOpen, // Accounting ledger
  holding: PieChart, // Portfolio holdings
  position: LineChart, // Trading positions
  global: Globe, // Global reports
  incomeTax: Percent, // Tax percentage
  depository: Database, // Data storage
  rms: Shield, // Risk management
  ipo: TrendingUp, // IPO trends
  liverisk: Globe, // Live risk monitoring
  mtf: CreditCard, // Margin trading facility
  stockVerification: ShieldCheck, // Stock verification
  ePledge: Landmark, // E-Pledge management
  mandate: WalletCards, // E-Mandate facility
  wealth: Briefcase, // Wealth management
  mutualFund: PieChart, // Fund allocation
  insurance: Shield, // Insurance protection
  pms: BarChart3, // Portfolio management
  downloads: Download, // File downloads
  helpDesk: HelpCircle, // Help and support
  faq: HelpCircle, // FAQ section
  setting: Settings, // Application settings
  search: Search, // Search functionality
  notifications: Bell, // Notifications
  settings: Settings, // Settings
  chevronRight: ChevronRight,
  chevronLeft: ChevronLeft,
  building: Building2, // Company/building
  Target: Target, // Stock scanner
  LineChart: LineChart, // market insight
  Newspaper: Newspaper, // News
} as const;

// Memoized Icon component for better performance
const IconComponent = React.memo<{
  icon: LucideIcon;
  className?: string;
  [key: string]: any;
}>(({ icon: Icon, className, ...props }) => <Icon className={className} {...props} />);

IconComponent.displayName = 'IconComponent';

// Custom hook for navigation state management
const useNavigationState = () => {
  const location = useLocation();

  const isActive = useCallback(
    (href?: string) => {
      if (!href) return false;

      // Exact match for the current path
      if (location.pathname === href) return true;

      // Special handling for IPO routes
      // /ipo should only be active when exactly on /ipo, not subroutes like /online or /physical
      if (href === '/ipo' && location.pathname !== href) {
        return false;
      }

      // For other routes, check if current path starts with the href
      return location.pathname.startsWith(href + '/');
    },
    [location.pathname],
  );

  const isParentActive = useCallback(
    (item: NavItem): boolean => {
      if (item.href && isActive(item.href)) return true;
      if (item.children) {
        return item.children?.some((child) => isParentActive(child)) ?? false;
      }
      return false;
    },
    [isActive],
  );

  return { isActive, isParentActive };
};

const Sidebar = ({ className, onNavigate }: SidebarProps) => {
  const { isActive, isParentActive } = useNavigationState();
  // Redux-driven collapsed state for desktop; mobile always expanded
  const { sidebarCollapsed } = useAppSelector((state) => state.ui);
  const { session } = useAppSelector((state) => state.user);
  const isDesktop = useMediaQuery('(min-width: 1024px)'); // Tailwind lg breakpoint
  const isExpanded = isDesktop ? !sidebarCollapsed : true;
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Get user data outside of useMemo to use in dependencies
  const userData = decryptData(
    sessionStorage.getItem('userData') || '',
    import.meta.env.VITE_APP_SALT as string,
  );

  const currentUser = session?.user || userData?.user;
  const currentUserType = (currentUser?.user_type || '').toLowerCase();
  const isTeamUser = currentUserType === 'team';
  const isAdminUser = currentUser?.branch_code === 'ADMIN' || currentUserType === 'admin';

  const navigationItems: NavItem[] = useMemo(
    () => [
      {
        id: 'dashboard',
        title: 'Dashboard',
        icon: ICONS.dashboard,
        href: '/dashboard',
      },
      {
        id: 'profile',
        title: 'Profile',
        icon: ICONS.profile,
        href: '/profile',
      },
      {
        id: 'kyc',
        title: 'KYC',
        icon: ICONS.kyc,
        href: '/kyc',
      },
      {
        id: 'wealth',
        title: 'Wealth',
        icon: ICONS.wealth,
        children: [
          {
            id: 'equity',
            title: 'Equity',
            icon: ICONS.insurance,
            href: '/wealth/equity',
          },
          {
            id: 'mutual-fund',
            title: 'Mutual Fund',
            icon: ICONS.mutualFund,
            href: '/wealth/mutual-fund',
          },

          {
            id: 'pms',
            title: 'PMS',
            icon: ICONS.pms,
            href: '/wealth/pms',
          },
        ],
      },
      {
        id: 'compliance',
        title: 'Compliance',
        icon: ICONS.rms,
        href: '/compliance',
      },
      {
        id: 'accounts',
        title: 'Accounts',
        icon: ICONS.accounts,
        children: [
          {
            id: 'brokerage-summary',
            title: 'Brokerage Summary',
            icon: ICONS.revenue,
            href: '/accounts/brokerage-summary',
          },
          {
            id: 'client-dr-cr',
            title: 'Client DR/CR',
            icon: ICONS.reports,
            href: '/accounts/client-dr-cr',
          },
          {
            id: 'aging',
            title: 'Ageing',
            icon: ICONS.aging,
            href: '/accounts/aging',
          },
          {
            id: 'late-payment-charges',
            title: 'Late Payment Charges',
            icon: ICONS.latePayment,
            href: '/accounts/late-payment-charges',
          },
          // {
          //   id: 'payout',
          //   title: 'Payout',
          //   icon: ICONS.rms,
          //   href: '/accounts/payout',
          // },
          // {
          //   id: 'risk-common',
          //   title: 'Risk Common',
          //   icon: ICONS.rms,
          //   href: '/accounts/risk-common',
          // },
        ],
      },
      {
        id: 'reports',
        title: 'Reports',
        icon: ICONS.reports,
        children: [
          {
            id: 'ledger',
            title: 'Ledger',
            icon: ICONS.ledger,
            href: '/reports/ledger',
          },
          {
            id: 'holding',
            title: 'Holding',
            icon: ICONS.holding,
            href: '/reports/holding',
          },
          {
            id: 'position',
            title: 'Position',
            icon: ICONS.position,
            href: '/reports/position',
          },
          // {
          //   id: 'global',
          //   title: 'Global',
          //   icon: ICONS.global,
          //   href: '/reports/global',
          // },
          // {
          //   id: 'income-tax',
          //   title: 'Income Tax',
          //   icon: ICONS.incomeTax,
          //   href: '/reports/income-tax',
          // },
          {
            id: 'contract-bill',
            title: 'Contract Bill',
            icon: ICONS.reports,
            href: '/reports/contract-bill',
          },
        ],
      },
      // {
      //   id: 'depository',
      //   title: 'Depository',
      //   icon: ICONS.depository,
      //   href: '/depository',
      // },
      // {
      //   id: 'rms',
      //   title: 'RMS',
      //   icon: ICONS.rms,
      //   href: '/rms',
      // },
      // # new ipo
      // # old ipo
      {
        id: IPO_BETA_ENABLED ? 'ipo-beta' : 'ipo',
        title: 'IPO',
        icon: ICONS.ipo,
        href: '/ipo',
        children: [
          {
            id: IPO_BETA_ENABLED ? 'ipo-beta-dashboard' : 'ipo-dashboard',
            title: 'Dashboard',
            icon: ICONS.dashboard,
            href: '/ipo',
          },
          {
            id: IPO_BETA_ENABLED ? 'ipo-beta-online' : 'ipo-online',
            title: 'Online',
            icon: ICONS.ipo,
            href: '/ipo/online',
          },
          {
            id: IPO_BETA_ENABLED ? 'ipo-beta-physical' : 'ipo-physical',
            title: 'Physical',
            icon: ICONS.ipo,
            href: '/ipo/physical',
          },
        ],
      },
      {
        id: 'stock-scanner',
        title: 'Stock Scanner',
        icon: ICONS.Target,
        href: '/stock-scanner',
      },
      {
        id: 'market-insight',
        title: 'Market Insight',
        icon: ICONS.LineChart,
        href: '/market-insight',
      },
      {
        id: 'news',
        title: 'News',
        icon: ICONS.Newspaper,
        href: '/news',
      },
      {
        id: 'mtf',
        title: 'MTF',
        icon: ICONS.mtf,
        href: '/mtf',
      },
      // {
      //   id: 'stock-verification',
      //   title: 'Stock Verification',
      //   icon: ICONS.stockVerification,
      //   href: '/stock-verification',
      // },
      {
        id: 'pledge',
        title: 'Pledge',
        icon: ICONS.ePledge,
        href: '/pledge',
      },
      {
        id: 'live-risk',
        title: 'Live Risk',
        icon: ICONS.liverisk,
        href: '/live-risk',
        badge: 'BETA',
      },
      {
        id: 'mandate',
        title: 'E-Mandate',
        icon: ICONS.mandate,
        href: '/mandate',
      },
      {
        id: 'downloads',
        title: 'Downloads',
        icon: ICONS.downloads,
        href: '/downloads',
      },
      // {
      //   id: 'help-desk',
      //   title: 'HelpDesk',
      //   icon: ICONS.helpDesk,
      //   href: '/help-desk',
      // },
      // {
      //   id: 'faq',
      //   title: 'FAQ',
      //   icon: ICONS.faq,
      //   href: '/faq',
      // },
      // {
      //   id: 'setting',
      //   title: 'Setting',
      //   icon: ICONS.setting,
      //   href: '/setting',
      // },
    ],
    [currentUser?.branch_code], // Add dependency to re-render when user changes
  );

  // Filter navigation items based on user role/type
  // Admins should not see Profile or Physical IPO links
  // Team users should not see Brokerage Summary under Accounts
  const filteredNavigationItems = useMemo(() => {
    let items = navigationItems;

    if (isAdminUser) {
      items =
        items
          ?.filter((item) => item.id !== 'profile')
          ?.map((item) => {
            if (item.children) {
              return {
                ...item,
                children:
                  item.children?.filter(
                    (c) => c.id !== 'ipo-physical' && c.id !== 'ipo-beta-physical',
                  ) ?? [],
              } as NavItem;
            }
            return item;
          }) ?? [];
    }

    if (isTeamUser) {
      items =
        items?.map((item) => {
          if (item.children) {
            return {
              ...item,
              children: item.children?.filter((c) => c.id !== 'brokerage-summary') ?? [],
            } as NavItem;
          }
          return item;
        }) ?? [];
    }

    return items;
  }, [navigationItems, isAdminUser, isTeamUser]);

  const isItemExpanded = useCallback(
    (itemId: string) => isExpanded && expandedItems.has(itemId),
    [isExpanded, expandedItems],
  );

  useMemo(() => {
    if (!isExpanded) return;

    const findActiveParents = (items: NavItem[]): string[] => {
      const activeParents: string[] = [];

      items?.forEach((item) => {
        if (item.children && isParentActive(item)) {
          activeParents.push(item.id);
        }
      });

      return activeParents;
    };

    const activeParents = findActiveParents(filteredNavigationItems);
    if (activeParents.length > 0) {
      setExpandedItems((prev) => new Set([...prev, ...activeParents]));
    }
  }, [filteredNavigationItems, isParentActive, isExpanded]);

  const toggleItemExpanded = useCallback((itemId: string) => {
    setExpandedItems((prev) => {
      // If clicking the currently expanded item, collapse it
      if (prev.has(itemId)) {
        return new Set(); // Close all
      } else {
        // Open only the clicked item, close all others
        return new Set([itemId]);
      }
    });
  }, []);

  const NavItemComponent = useCallback(
    ({ item, level = 0 }: { item: NavItem; level?: number }) => {
      const hasChildren = item.children && item.children.length > 0;
      const active = item.href ? isActive(item.href) : isParentActive(item);
      const expanded = isItemExpanded(item.id);

      const paddingLeft = level === 0 ? 'pl-4' : level === 1 ? 'pl-10' : 'pl-16';

      return (
        <div key={item.id}>
          {hasChildren ? (
            <>
              <button
                onClick={() => toggleItemExpanded(item.id)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl py-3 pr-4 text-sm transition-all duration-200',
                  'group text-left hover:bg-[#118AB2]/10 dark:hover:bg-[#118AB2]/20',
                  paddingLeft,
                  active && 'bg-[#118AB2]/10 dark:bg-[#118AB2]/20',
                )}
              >
                {item.icon && (
                  <IconComponent
                    icon={item.icon}
                    className="h-5 w-5 shrink-0 text-gray-600 transition-colors group-hover:text-[#118AB2] dark:text-gray-400"
                  />
                )}
                <div className="flex min-w-0 flex-1 items-center">
                  <span className="truncate font-medium text-gray-700 transition-colors group-hover:text-[#118AB2] dark:text-gray-300">
                    {item.title}
                  </span>
                  {item.badge && (
                    <span className="ml-1.5 inline-flex -translate-y-1 items-center rounded-md border border-[#118AB2]/30 bg-[#118AB2]/10 px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none tracking-wider text-[#118AB2] dark:border-[#06D6A0]/30 dark:bg-[#06D6A0]/15 dark:text-[#06D6A0]">
                      {item.badge}
                    </span>
                  )}
                </div>
                <div className="shrink-0">
                  <ICONS.chevronRight
                    className={cn(
                      'h-4 w-4 text-gray-400 transition-all duration-200 group-hover:text-[#118AB2]',
                      expanded && 'rotate-90',
                    )}
                  />
                </div>
              </button>
              {expanded && (
                <div className="mt-1 space-y-1 duration-300 animate-in fade-in slide-in-from-top-2 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:slide-out-to-top-2">
                  {item.children?.map((child, idx) => {
                    const childActive = child.href ? isActive(child.href) : false;

                    return (
                      <Link
                        key={child.id}
                        to={child.href || '#'}
                        onClick={() => onNavigate?.()}
                        style={{ animationDelay: `${idx * 50}ms` }}
                        className={cn(
                          'group relative flex items-center gap-3 rounded-xl py-2.5 pl-2 text-sm transition-all duration-300',
                          'animate-in fade-in slide-in-from-left-2',
                          !childActive && 'hover:bg-[#118AB2]/10',
                          childActive && 'bg-[#118AB2] shadow-[#118AB2]/20',
                        )}
                      >
                        {child.icon ? (
                          <div
                            className={cn(
                              'rounded-lg p-1.5 transition-all duration-300',
                              childActive
                                ? 'bg-white/20 shadow-inner'
                                : 'bg-gray-100 group-hover:bg-white group-hover:shadow-sm dark:bg-gray-800',
                            )}
                          >
                            <IconComponent
                              icon={child.icon}
                              className={cn(
                                'h-4 w-4 shrink-0 transition-colors',
                                childActive ? 'text-white' : 'text-[#118AB2] dark:text-gray-400',
                              )}
                            />
                          </div>
                        ) : (
                          <div
                            className={cn(
                              'flex h-6 w-6 items-center justify-center rounded-lg text-xs font-bold',
                              childActive
                                ? 'bg-white/20 text-white'
                                : 'bg-gray-100 text-gray-600 group-hover:bg-white group-hover:text-[#118AB2] dark:bg-gray-800 dark:text-gray-400',
                            )}
                          >
                            {idx + 1}
                          </div>
                        )}
                        <div className="flex min-w-0 flex-1 items-center">
                          <span
                            className={cn(
                              'truncate font-semibold transition-colors',
                              childActive
                                ? 'text-white'
                                : 'text-gray-700 group-hover:text-[#118AB2] dark:text-gray-300',
                            )}
                          >
                            {child.title}
                          </span>
                          {child.badge && (
                            <span
                              className={cn(
                                'shadow-xs ml-1.5 inline-flex -translate-y-1 items-center rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none tracking-wider',
                                childActive
                                  ? 'border border-white/30 bg-white/25 text-white backdrop-blur-sm'
                                  : 'border border-[#118AB2]/30 bg-[#118AB2]/10 text-[#118AB2] dark:border-[#06D6A0]/30 dark:bg-[#06D6A0]/15 dark:text-[#06D6A0]',
                              )}
                            >
                              {child.badge}
                            </span>
                          )}
                        </div>
                        {child.comingSoon && (
                          <span className="animate-pulse rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-2 py-1 text-xs font-bold text-white shadow-md shadow-amber-500/30">
                            New
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </>
          ) : item.href ? (
            <Link
              to={item.href}
              onClick={() => onNavigate?.()}
              className={cn(
                'flex items-center gap-3 rounded-xl py-3 pr-4 text-sm transition-all duration-200',
                'group hover:bg-gray-100 dark:hover:bg-gray-800',
                paddingLeft,
                active && 'bg-[#118AB2] text-white shadow-md hover:bg-[#118AB2]',
              )}
            >
              {item.icon && (
                <IconComponent
                  icon={item.icon}
                  className={cn(
                    'h-5 w-5 shrink-0 transition-colors',
                    active
                      ? 'text-white'
                      : 'text-gray-600 group-hover:text-[#118AB2] dark:text-gray-400',
                  )}
                />
              )}
              <div className="flex min-w-0 flex-1 items-center">
                <span
                  className={cn(
                    'truncate font-medium transition-colors',
                    active
                      ? 'text-white'
                      : 'text-gray-700 group-hover:text-[#118AB2] dark:text-gray-300',
                  )}
                >
                  {item.title}
                </span>
                {item.badge && (
                  <span
                    className={cn(
                      'shadow-xs ml-1.5 inline-flex -translate-y-1 items-center rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none tracking-wider',
                      active
                        ? 'border border-white/30 bg-white/25 text-white backdrop-blur-sm'
                        : 'border border-[#118AB2]/30 bg-[#118AB2]/10 text-[#118AB2] dark:border-[#06D6A0]/30 dark:bg-[#06D6A0]/15 dark:text-[#06D6A0]',
                    )}
                  >
                    {item.badge}
                  </span>
                )}
              </div>
              {item.comingSoon && (
                <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                  Soon
                </span>
              )}
            </Link>
          ) : (
            <button
              className={cn(
                'flex w-full items-center gap-3 rounded-xl py-3 pr-4 text-sm transition-all duration-200',
                'group text-left hover:bg-[#118AB2]/10 dark:hover:bg-[#118AB2]/20',
                paddingLeft,
                active && 'bg-[#118AB2]/10 dark:bg-[#118AB2]/20',
              )}
            >
              {item.icon && (
                <IconComponent
                  icon={item.icon}
                  className="h-5 w-5 shrink-0 text-gray-600 transition-colors group-hover:text-[#118AB2] dark:text-gray-400"
                />
              )}
              <div className="flex min-w-0 flex-1 items-center">
                <span className="truncate font-medium text-gray-700 transition-colors group-hover:text-[#118AB2] dark:text-gray-300">
                  {item.title}
                </span>
                {item.badge && (
                  <span className="ml-1.5 inline-flex -translate-y-1 items-center rounded-md border border-[#118AB2]/30 bg-[#118AB2]/10 px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none tracking-wider text-[#118AB2] dark:border-[#06D6A0]/30 dark:bg-[#06D6A0]/15 dark:text-[#06D6A0]">
                    {item.badge}
                  </span>
                )}
              </div>
            </button>
          )}
        </div>
      );
    },
    [isItemExpanded, isActive, isParentActive],
  );

  const renderCollapsedNav = useMemo(
    () => (
      <div className="space-y-3">
        {filteredNavigationItems?.map((item) => {
          const active = item.href ? isActive(item.href) : isParentActive(item);
          const hasChildren = item.children && item.children.length > 0;

          if (!item.icon) return null;

          return (
            <div key={item.id}>
              {hasChildren ? (
                <DropdownMenu>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={cn(
                              'flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-200',
                              'group hover:scale-105 hover:bg-[#118AB2]/10 dark:hover:bg-[#118AB2]/20',
                              active && 'bg-[#118AB2] shadow-lg',
                            )}
                          >
                            <IconComponent
                              icon={item.icon}
                              className={cn(
                                'h-6 w-6 transition-all duration-200',
                                active
                                  ? 'text-white'
                                  : 'text-gray-600 group-hover:text-[#118AB2] dark:text-gray-400',
                              )}
                            />
                            {active && (
                              <div className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full bg-white shadow-sm" />
                            )}
                          </button>
                        </DropdownMenuTrigger>
                      </TooltipTrigger>
                      <TooltipContent
                        side="right"
                        align="center"
                        className="rounded-lg border-0 bg-gray-900 text-white shadow-xl"
                        sideOffset={12}
                      >
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium">{item.title}</p>
                          {item.badge && (
                            <span className="rounded bg-white/20 px-1 py-0.5 text-[8px] font-bold uppercase leading-none text-white">
                              {item.badge}
                            </span>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <DropdownMenuContent
                    side="right"
                    align="start"
                    className="w-64 rounded-xl border-0 bg-white p-2 shadow-xl dark:bg-gray-900"
                    sideOffset={12}
                  >
                    <div className="mb-2 border-b border-gray-100 px-3 py-2 text-sm font-semibold text-gray-600 dark:border-gray-800 dark:text-gray-400">
                      <div className="flex items-center gap-1.5">
                        <span>{item.title}</span>
                        {item.badge && (
                          <span className="rounded bg-[#118AB2]/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#118AB2] dark:bg-[#06D6A0]/20 dark:text-[#06D6A0]">
                            {item.badge}
                          </span>
                        )}
                      </div>
                    </div>
                    {item.children?.map((child) => {
                      const childActive = child.href ? isActive(child.href) : false;

                      return (
                        <DropdownMenuItem key={child.id} asChild className="p-0">
                          <Link
                            to={child.href || '#'}
                            onClick={() => onNavigate?.()}
                            className={cn(
                              'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-200',
                              'group hover:bg-[#118AB2]/10 dark:hover:bg-[#118AB2]/20',
                              childActive && 'bg-[#118AB2] text-white shadow-sm',
                            )}
                          >
                            {child.icon && (
                              <IconComponent
                                icon={child.icon}
                                className={cn(
                                  'h-4 w-4 shrink-0 transition-colors',
                                  childActive
                                    ? 'text-white'
                                    : 'text-gray-500 group-hover:text-[#118AB2] dark:text-gray-400 dark:group-hover:text-[#118AB2]',
                                )}
                              />
                            )}
                            <div className="flex min-w-0 flex-1 items-center">
                              <span
                                className={cn(
                                  'truncate font-medium transition-colors',
                                  childActive
                                    ? 'text-white'
                                    : 'text-gray-700 group-hover:text-[#118AB2] dark:text-gray-300 dark:group-hover:text-[#118AB2]',
                                )}
                              >
                                {child.title}
                              </span>
                              {child.badge && (
                                <span
                                  className={cn(
                                    'shadow-xs ml-1.5 inline-flex -translate-y-1 items-center rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none tracking-wider',
                                    childActive
                                      ? 'border border-white/30 bg-white/25 text-white backdrop-blur-sm'
                                      : 'border border-[#118AB2]/30 bg-[#118AB2]/10 text-[#118AB2] dark:border-[#06D6A0]/30 dark:bg-[#06D6A0]/15 dark:text-[#06D6A0]',
                                  )}
                                >
                                  {child.badge}
                                </span>
                              )}
                            </div>
                            {child.comingSoon && (
                              <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                                Soon
                              </span>
                            )}
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : item.href ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to={item.href}
                        onClick={() => onNavigate?.()}
                        className={cn(
                          'relative flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-200',
                          'group hover:scale-105 hover:bg-gray-100 dark:hover:bg-gray-800',
                          active && 'bg-[#118AB2] shadow-lg',
                        )}
                      >
                        <IconComponent
                          icon={item.icon}
                          className={cn(
                            'h-6 w-6 transition-all duration-200',
                            active
                              ? 'text-white'
                              : 'text-gray-600 group-hover:text-[#118AB2] dark:text-gray-400 dark:group-hover:text-[#118AB2]',
                          )}
                        />
                        {active && (
                          <div className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full bg-white shadow-sm" />
                        )}
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      align="center"
                      className="rounded-lg border-0 bg-gray-900 text-white shadow-xl"
                      sideOffset={12}
                    >
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium">{item.title}</p>
                        {item.badge && (
                          <span className="rounded bg-white/20 px-1 py-0.5 text-[8px] font-bold uppercase leading-none text-white">
                            {item.badge}
                          </span>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className={cn(
                          'relative flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-200',
                          'group hover:scale-105 hover:bg-gray-100 dark:hover:bg-gray-800',
                          active && 'bg-[#118AB2] shadow-lg',
                        )}
                      >
                        <IconComponent
                          icon={item.icon}
                          className={cn(
                            'h-6 w-6 transition-all duration-200',
                            active
                              ? 'text-white'
                              : 'text-gray-600 group-hover:text-[#118AB2] dark:text-gray-400 dark:group-hover:text-[#118AB2]',
                          )}
                        />
                        {active && (
                          <div className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full bg-white shadow-sm" />
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      align="center"
                      className="rounded-lg border-0 bg-gray-900 text-white shadow-xl"
                      sideOffset={12}
                    >
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium">{item.title}</p>
                        {item.badge && (
                          <span className="rounded bg-white/20 px-1 py-0.5 text-[8px] font-bold uppercase leading-none text-white">
                            {item.badge}
                          </span>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          );
        })}
      </div>
    ),
    [filteredNavigationItems, isActive, isParentActive],
  );

  return (
    <div
      className={cn(
        'bg-white transition-all duration-300 dark:bg-gray-900',
        'border-r border-gray-200 shadow-lg dark:border-gray-800',
        isExpanded ? 'w-64' : 'w-20',
        className,
      )}
    >
      <div className="space-y-4 pb-2 pt-3.5">
        <div className={cn('px-4', !isExpanded && 'px-4')}>
          <div className={cn('flex items-center', isExpanded ? 'gap-3' : 'justify-center')}>
            {isExpanded ? (
              <>
                <div className="relative">
                  <img src={ArhamLogo} alt="ARHAMSHARE" className="w-52" />
                </div>
              </>
            ) : (
              <div className="relative">
                <img src={SmallArhamLogo} alt="ARHAMSHARE" className="h-8 w-10 object-contain" />
                {/* <div className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border border-white bg-green-500"></div> */}
              </div>
            )}
          </div>
        </div>

        <Separator className="bg-gray-200 dark:bg-gray-700" />

        <div className={cn('px-4', !isExpanded && 'px-2')}>
          <ScrollArea className="h-[calc(100vh-90px)]" hideScrollbar={true}>
            {isExpanded ? (
              <div className="space-y-2">
                {filteredNavigationItems?.map((item) => (
                  <NavItemComponent key={item.id} item={item} />
                ))}
              </div>
            ) : (
              renderCollapsedNav
            )}
          </ScrollArea>
        </div>

        {isExpanded ? (
          <></>
        ) : (
          // <div className="space-y-2">
          //   <div className="mb-3 px-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          //     Quick Actions
          //   </div>
          //   <Link
          //     to="/search"
          //     className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-800"
          //   >
          //     <ICONS.search className="h-5 w-5 text-gray-500 transition-colors group-hover:text-[#118AB2] dark:text-gray-400 dark:group-hover:text-[#118AB2]" />
          //     <span className="font-medium text-gray-700 dark:text-gray-300">Search</span>
          //   </Link>
          //   {/* Hide notifications for ADMIN users */}
          //   {userData?.user?.branch_code !== 'ADMIN' && (
          //     <Link
          //       to="/notifications"
          //       className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-800"
          //     >
          //       <ICONS.notifications className="h-5 w-5 text-gray-500 transition-colors group-hover:text-[#118AB2] dark:text-gray-400 dark:group-hover:text-[#118AB2]" />
          //       <span className="font-medium text-gray-700 dark:text-gray-300">
          //         Notifications
          //       </span>
          //       <span className="ml-auto rounded-full bg-red-500 px-2 py-1 text-xs font-medium text-white shadow-sm">
          //         3
          //       </span>
          //     </Link>
          //   )}
          //   <Link
          //     to="/settings"
          //     className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-800"
          //   >
          //     <ICONS.settings className="h-5 w-5 text-gray-500 transition-colors group-hover:text-[#118AB2] dark:text-gray-400 dark:group-hover:text-[#118AB2]" />
          //     <span className="font-medium text-gray-700 dark:text-gray-300">Settings</span>
          //   </Link>
          // </div>
          <></>
          // <div className="space-y-3">
          //   <TooltipProvider>
          //     <Tooltip>
          //       <TooltipTrigger asChild>
          //         <Link
          //           to="/search"
          //           className="group relative flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-200 hover:scale-105 hover:bg-[#118AB2]/10 dark:hover:bg-[#118AB2]/20"
          //         >
          //           <ICONS.search className="h-6 w-6 text-gray-600 transition-colors group-hover:text-[#118AB2] dark:text-gray-400 dark:group-hover:text-[#118AB2]" />
          //         </Link>
          //       </TooltipTrigger>
          //       <TooltipContent
          //         side="right"
          //         align="center"
          //         className="rounded-lg border-0 bg-gray-900 text-white shadow-xl"
          //         sideOffset={12}
          //       >
          //         <p className="font-medium">Search</p>
          //       </TooltipContent>
          //     </Tooltip>
          //   </TooltipProvider>

          //   {/* Hide notifications for ADMIN users */}
          //   {userData?.user?.branch_code !== 'ADMIN' && (
          //     <TooltipProvider>
          //       <Tooltip>
          //         <TooltipTrigger asChild>
          //           <Link
          //             to="/notifications"
          //             className="group relative flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-200 hover:scale-105 hover:bg-[#118AB2]/10 dark:hover:bg-[#118AB2]/20"
          //           >
          //             <ICONS.notifications className="h-6 w-6 text-gray-600 transition-colors group-hover:text-[#118AB2] dark:text-gray-400 dark:group-hover:text-[#118AB2]" />
          //             <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
          //               3
          //             </span>
          //           </Link>
          //         </TooltipTrigger>
          //         <TooltipContent
          //           side="right"
          //           align="center"
          //           className="rounded-lg border-0 bg-gray-900 text-white shadow-xl"
          //           sideOffset={12}
          //         >
          //           <p className="font-medium">Notifications (3)</p>
          //         </TooltipContent>
          //       </Tooltip>
          //     </TooltipProvider>
          //   )}

          //   <TooltipProvider>
          //     <Tooltip>
          //       <TooltipTrigger asChild>
          //         <Link
          //           to="/settings"
          //           className="group relative flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-200 hover:scale-105 hover:bg-[#118AB2]/10 dark:hover:bg-[#118AB2]/20"
          //         >
          //           <ICONS.settings className="h-6 w-6 text-gray-600 transition-colors group-hover:text-[#118AB2] dark:text-gray-400 dark:group-hover:text-[#118AB2]" />
          //         </Link>
          //       </TooltipTrigger>
          //       <TooltipContent
          //         side="right"
          //         align="center"
          //         className="rounded-lg border-0 bg-gray-900 text-white shadow-xl"
          //         sideOffset={12}
          //       >
          //         <p className="font-medium">Settings</p>
          //       </TooltipContent>
          //     </Tooltip>
          //   </TooltipProvider>
          // </div>
        )}
        {/* </div> */}
      </div>
    </div>
  );
};

// Memoize the entire Sidebar component for better performance
const MemoizedSidebar = React.memo(Sidebar);
MemoizedSidebar.displayName = 'Sidebar';

export { MemoizedSidebar as Sidebar };
export default MemoizedSidebar;
